import { useEffect } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import type { BadgeTone } from "../../types/vtc";
import { STATUS_TONES } from "../../data/company";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";
import Badge from "../ui/Badge";

/** Building blocks shared by the company area pages, as in the design. */

export function Card({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn("min-w-0 rounded-card bg-white p-5", className)}>
      {children}
    </section>
  );
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cn("m-0 mb-2 text-base font-medium", className)}>{children}</h2>;
}

export function PageTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h1 className={cn("m-0 mb-4.5 text-[26px] font-medium tracking-[-0.01em]", className)}>
      {children}
    </h1>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-[13px] text-muted no-underline hover:text-ink">
      ← {children}
    </Link>
  );
}

type StatusKind = keyof typeof STATUS_TONES;

/** Coloured status pill; tone from STATUS_TONES, label from t.company.status. */
export function StatusBadge<K extends StatusKind>({
  kind,
  status,
  className,
}: {
  kind: K;
  status: keyof (typeof STATUS_TONES)[K] & string;
  className?: string;
}) {
  const t = useT();
  const tone = (STATUS_TONES[kind] as Record<string, BadgeTone>)[status];
  const label = (t.company.status[kind] as Record<string, string>)[status];
  return (
    <Badge tone={tone} className={className}>
      {label}
    </Badge>
  );
}

export function Hint({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("m-0 text-xs leading-normal text-faint", className)}>{children}</p>;
}

/** Small key / value row used in detail cards. */
export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-surface py-2.25 text-sm">
      <span className="text-muted">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

const ACTION_VARIANTS = {
  primary: "bg-brand text-white hover:bg-brand-dark",
  dark: "bg-ink text-white hover:bg-ink-soft",
  red: "bg-tone-red-fg text-white hover:bg-[#912018]",
  grey: "bg-surface text-ink hover:bg-surface-hover",
  white: "bg-white text-ink hover:bg-[#FAFAFA]",
  // Inset shadows draw the 1px outlines (the base class resets borders).
  danger: "bg-white text-tone-red-fg shadow-[inset_0_0_0_1px_var(--color-tone-red-fg)] hover:bg-tone-red-bg",
  outlineRed: "bg-white text-tone-red-fg shadow-[inset_0_0_0_1px_#E4E4E4] hover:bg-tone-red-bg",
  dangerText: "bg-white text-tone-red-fg hover:bg-tone-red-bg",
  quiet: "bg-white text-muted hover:text-ink",
};

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof ACTION_VARIANTS;
  size?: "sm" | "md" | "lg";
}

/** The design's compact buttons: 30 / 36 / 44px tall. */
export function ActionButton({
  variant = "grey",
  size = "md",
  className,
  type = "button",
  ...rest
}: ActionButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded border-0 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" && "h-7.5 px-2.5 text-xs",
        size === "md" && "h-9 px-3.5 text-[13px]",
        size === "lg" && "h-11 px-5 text-sm",
        ACTION_VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
}

interface ModalProps {
  title: string;
  text?: ReactNode;
  error?: string;
  /** Primary button; omit for an info-only modal. */
  okLabel?: string;
  okVariant?: "primary" | "dark" | "danger";
  cancelLabel?: string;
  onOk?: () => void;
  onClose: () => void;
  children?: ReactNode;
}

export function Modal({
  title,
  text,
  error,
  okLabel,
  okVariant = "primary",
  cancelLabel,
  onOk,
  onClose,
  children,
}: ModalProps) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-80 flex items-center justify-center bg-ink/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full max-w-130 overflow-y-auto rounded-card bg-white p-6"
      >
        <h2 className="m-0 mb-2 text-xl font-medium">{title}</h2>
        {text && <p className="m-0 mb-4 text-sm leading-normal text-muted">{text}</p>}
        {children}
        {error && <p className="mt-2 mb-0 text-[13px] text-tone-red-fg">{error}</p>}
        <div className="mt-4.5 flex flex-wrap justify-end gap-2">
          <ActionButton onClick={onClose} className="h-10.5 px-4 text-sm font-normal">
            {cancelLabel ?? t.common.cancel}
          </ActionButton>
          {okLabel && (
            <ActionButton
              variant={okVariant === "danger" ? "red" : okVariant}
              onClick={onOk}
              className="h-10.5 px-4.5 text-sm"
            >
              {okLabel}
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
}

/** Radio card used in modals (vehicle pick, Urgent duration). */
export function ChoiceRow({
  name,
  checked,
  disabled,
  onChange,
  children,
}: {
  name: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2.5",
        checked ? "border-brand" : "border-line-strong",
        disabled && "cursor-not-allowed opacity-55",
      )}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="accent-brand"
      />
      {children}
    </label>
  );
}

/** Message bubbles for booking threads. */
export function MessageBubble({
  from,
  text,
  meta,
  me = "company",
}: {
  from: "company" | "driver" | "system";
  text: string;
  meta: string;
  /** Whose screen this is: their own messages are blue, on the right. */
  me?: "company" | "driver";
}) {
  return (
    <div
      className={cn(
        "max-w-[85%] whitespace-pre-line rounded-card px-3 py-2.5 text-sm leading-[1.45]",
        from === me && "justify-self-end bg-brand text-white",
        from !== me && from !== "system" && "justify-self-start bg-surface text-ink",
        from === "system" && "justify-self-start bg-tone-blue-bg text-ink",
      )}
    >
      {text}
      <div className="mt-1 text-[11px] opacity-70">{meta}</div>
    </div>
  );
}

export function MessageInput({
  value,
  onChange,
  onSend,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  placeholder: string;
}) {
  const t = useT();
  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSend();
      }}
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 min-w-0 flex-1 rounded border-0 bg-surface px-3 text-sm outline-none focus:ring-1 focus:ring-brand"
      />
      <ActionButton type="submit" variant="dark" className="h-10 px-4 text-sm font-normal">
        {t.common.send}
      </ActionButton>
    </form>
  );
}
